// localStorage-backed saved favorites (max 20). Stores a minimal snapshot of the
// SearchItem so the home view can render + navigate without re-querying the index.

import { SearchItem } from "./types";

const KEY = "jvvnl_search_favorites_v1";
const MAX = 20;

// Full SearchItem snapshot + recency timestamp, so the stored favorite can be
// navigated/rendered identically to a live search result.
export interface FavoriteItem extends SearchItem {
  /** epoch ms of last save/open — used for recency sort. */
  ts: number;
}

function read(): FavoriteItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(items: FavoriteItem[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items.slice(0, MAX)));
  } catch {
    /* quota / unavailable — degrade silently */
  }
}

export function getFavorites(): FavoriteItem[] {
  // Most recently opened first (manual pin order is future work).
  return read().sort((a, b) => b.ts - a.ts);
}

export function isFavorite(id: string): boolean {
  return read().some((f) => f.id === id);
}

function toFavorite(item: SearchItem): FavoriteItem {
  return { ...item, ts: Date.now() };
}

export function addFavorite(item: SearchItem): FavoriteItem[] {
  const existing = read().filter((f) => f.id !== item.id);
  const next = [toFavorite(item), ...existing].slice(0, MAX);
  write(next);
  return getFavorites();
}

export function removeFavorite(id: string): FavoriteItem[] {
  write(read().filter((f) => f.id !== id));
  return getFavorites();
}

export function toggleFavorite(item: SearchItem): FavoriteItem[] {
  return isFavorite(item.id) ? removeFavorite(item.id) : addFavorite(item);
}

/** Bump recency when a favorite is opened (so home view sorts it to the top). */
export function touchFavorite(id: string): void {
  const items = read();
  const f = items.find((x) => x.id === id);
  if (!f) return;
  f.ts = Date.now();
  write(items);
}
